
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    if (!isHomePage) {
      window.location.href = `/#${id}`;
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'Process', id: 'process' },
    { label: 'Services', href: '/services' },
    { label: 'Gallery', id: 'gallery' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Merch', id: 'merchandise' },
    { label: 'Contact', id: 'contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'py-3' 
          : 'py-4'
      }`}
      style={{
        background: 'linear-gradient(135deg, rgba(0, 50, 80, 0.95) 0%, rgba(0, 80, 120, 0.95) 50%, rgba(0, 60, 100, 0.95) 100%)',
        boxShadow: '0 4px 40px rgba(0, 212, 255, 0.4), 0 0 60px rgba(0, 180, 255, 0.2), inset 0 1px 0 rgba(0, 212, 255, 0.3)',
        borderBottom: '1px solid rgba(0, 212, 255, 0.4)'
      }}
    >
      {/* Top glow line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
      
      {/* Bottom glow line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-blue to-transparent"></div>

      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 cursor-pointer group">
          <img
            src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/8ce09f35fa7806c6d32ab167cebd341b.png"
            alt="Marche Logo"
            className="w-10 h-10 object-contain group-hover:scale-110 transition-transform duration-300"
            style={{ filter: 'drop-shadow(0 0 15px rgba(0, 212, 255, 1))' }}
          />
          <span 
            className="text-2xl font-orbitron font-bold text-white"
            style={{ textShadow: '0 0 30px rgba(0, 212, 255, 1), 0 0 60px rgba(0, 212, 255, 0.5)' }}
          >
            MARCHE
          </span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            item.href ? (
              <Link
                key={item.label}
                to={item.href}
                className="text-white hover:text-cyan-300 font-inter text-sm font-medium transition-all duration-300 relative group cursor-pointer"
                style={{ textShadow: '0 0 10px rgba(255, 255, 255, 0.3)' }}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-blue to-cyan-400 group-hover:w-full transition-all duration-300" style={{ boxShadow: '0 0 10px rgba(0, 212, 255, 0.8)' }}></span>
              </Link>
            ) : (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.id!)}
                className="text-white hover:text-cyan-300 font-inter text-sm font-medium transition-all duration-300 relative group cursor-pointer"
                style={{ textShadow: '0 0 10px rgba(255, 255, 255, 0.3)' }}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-blue to-cyan-400 group-hover:w-full transition-all duration-300" style={{ boxShadow: '0 0 10px rgba(0, 212, 255, 0.8)' }}></span>
              </button>
            )
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href="https://wa.me/12694649163"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-white hover:text-green-400 transition-all duration-300 cursor-pointer"
            style={{ textShadow: '0 0 15px rgba(34, 197, 94, 0.6)' }}
          >
            <i className="ri-whatsapp-line text-xl"></i>
            <span className="font-inter text-sm font-medium">(269) 464-9163</span>
          </a>
          <button
            onClick={() => scrollToSection('contact')}
            className="px-6 py-2.5 bg-gradient-to-r from-cyan-400 to-teal-400 text-black font-inter font-bold text-sm rounded-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ boxShadow: '0 0 25px rgba(0, 212, 255, 0.7)' }}
          >
            BOOK NOW
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
        >
          <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden absolute top-full left-0 right-0 border-t border-primary-blue/30 py-6"
          style={{
            background: 'linear-gradient(180deg, rgba(0, 60, 100, 0.98) 0%, rgba(0, 40, 70, 0.98) 100%)',
            boxShadow: '0 10px 40px rgba(0, 212, 255, 0.3)'
          }}
        >
          <div className="max-w-7xl mx-auto px-6 flex flex-col gap-4">
            {navItems.map((item) => (
              item.href ? (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white hover:text-cyan-300 font-inter text-base font-medium py-2 transition-all duration-300 cursor-pointer"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.id!)}
                  className="text-white hover:text-cyan-300 font-inter text-base font-medium py-2 transition-all duration-300 text-left cursor-pointer"
                >
                  {item.label}
                </button>
              )
            ))}
            <a
              href="https://wa.me/12694649163"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-green-400 font-inter text-base font-medium py-2 cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              (269) 464-9163
            </a>
            <button
              onClick={() => scrollToSection('contact')}
              className="mt-2 px-6 py-3 bg-gradient-to-r from-cyan-400 to-teal-400 text-black font-inter font-bold text-sm rounded-lg whitespace-nowrap cursor-pointer"
              style={{ boxShadow: '0 0 25px rgba(0, 212, 255, 0.6)' }}
            >
              BOOK NOW
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
